import React, { Component } from 'react'
import { observer, inject } from 'mobx-react'
import { computed, extendObservable } from 'mobx'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router'
import DocumentTitle from 'react-document-title'
import Action from '@components/Action'
import AddIcon from '@components/Icon/Add'
import Button from '@components/Button'
import { formatDate, getFeeItem, formatNumber } from '@atheneum/api/dist/lib'

import './Invoice.sass'
import InvoicesTable from './Components/InvoicesTable'
import NoContent from '@components/Error/NoContent'
import ACL from '@components/ACL'
import InfoBox from '@components/InfoBox'

class InvoiceList extends Component {
  static propTypes = {
    invoiceStore: PropTypes.object,
    deliverableStore: PropTypes.object,
    modalStore: PropTypes.object,
    staticData: PropTypes.object,
    invoiceType: PropTypes.number.isRequired,
    project: PropTypes.object,
    contract: PropTypes.object
  }

  constructor(props) {
    super(props)
    const {
      invoiceStore,
      staticData,
      deliverableStore,
      invoiceType,
      contract,
      project
    } = this.props

    extendObservable(this, {
      error: null,
      invoiceTypeName: computed(
        () =>
          staticData.names.invoiceTypes[invoiceType] || 'Unknown invoice type'
      ),
      isProjectBased: computed(
        () => staticData.CONST.INVOICE_TYPES.PAYG === invoiceType
      ),
      isContractBased: computed(
        () => staticData.CONST.INVOICE_TYPES.NON_PAYG === invoiceType
      )
    })

    if (this.isProjectBased) {
      invoiceStore.getListByProject(project.id)
      deliverableStore.getByProject(project.id, { paygOnly: true })
    } else if (this.isContractBased) {
      invoiceStore.getListByContract(contract.id)
      deliverableStore.getByContract(contract.id)
    } else {
      this.error = 'Invoice has an invalid invoice type, cannot continue.'
    }
  }

  handleCreatePayment = () => {
    return this.props.modalStore.show({
      type: 'INVOICE_PAYMENT_FORM',
      props: {
        title: 'Add payment',
        cancelText: 'Cancel',
        okText: 'Add payment',
        invoice: true,
        invoiceType: this.props.invoiceType
      }
    })
  }

  handleAddFee = () => {
    return this.props.modalStore.show({
      type: 'INVOICE_ADD_FEES_FORM',
      props: {
        title: 'Add fees',
        cancelText: 'Cancel',
        okText: 'Add to invoice',
        invoice: true,
        invoiceType: this.props.invoiceType
      }
    })
  }

  handleDraftNewInvoice = () => {
    const { contract, project, invoiceType, modalStore } = this.props

    return modalStore.show({
      type: 'INVOICE_FORM',
      props: {
        title: 'Draft new invoice',
        cancelText: 'Cancel',
        okText: 'Draft invoice',
        invoiceType,
        contract,
        project
      }
    })
  }

  handleCreateInvoiceItems = () => {
    return this.props.modalStore.show({
      type: 'INVOICE_ITEMS_FORM',
      props: {
        title: 'Add invoice items',
        cancelText: 'Cancel',
        okText: 'Create',
        project: this.props.project,
        invoiceType: this.props.invoiceType
      }
    })
  }

  getTitle = () => {
    const { project, contract } = this.props

    if (this.isProjectBased) {
      const projectName = project.projectName
      return `${projectName} - invoicing | Atheneum Partners`
    } else if (this.isContractBased) {
      const contractReference = contract.contractReference
      return `${contractReference} - invoicing | Atheneum Partners`
    } else {
      return `Invoicing | Atheneum Partners`
    }
  }

  renderUnallocatedFees = () => {
    const { unallocatedFees } = this.props.deliverableStore

    if (!unallocatedFees.length) {
      return <NoContent label='There are no unallocated fees' />
    }

    let contractOrProjectColumn = null
    if (this.isProjectBased) {
      contractOrProjectColumn = 'Contract'
    } else if (this.isContractBased) {
      contractOrProjectColumn = 'Project'
    }

    return (
      <table className='table-wrapper--nested invoice-table'>
        <thead>
          <tr>
            <th className='invoice-table__fee-name-column'>Fee type</th>
            <th>Delivery date</th>
            <th>{contractOrProjectColumn}</th>
            <th className='invoice-table__fee-amount-column'>Amount</th>
          </tr>
        </thead>

        <tbody>{this.renderFees(unallocatedFees)}</tbody>
      </table>
    )
  }

  renderFees = fees => {
    const { staticData } = this.props
    return fees.map(fee => {
      const feeItem = getFeeItem(fee)
      const feeItemType = staticData.names.feeTypes[feeItem.feeTypeId]
      const currency = staticData.names.currencies[feeItem.currencyId]
      const feeItemAmount = feeItem.value || 0

      let contractOrProjectColumn = '-'
      if (this.isProjectBased) {
        contractOrProjectColumn = fee.contract
          ? fee.contract.contractReference
          : '-'
      } else if (this.isContractBased) {
        contractOrProjectColumn = fee.project ? fee.project.projectName : '-'
      }

      return (
        <tr key={fee.id}>
          <td>
            {fee.parentName} - {feeItemType} <br />
            {fee.expert ? `(${fee.expert.title} ${fee.expert.fullName})` : ''}
          </td>
          <td>{fee.deliveryDate ? formatDate(fee.deliveryDate) : '-'}</td>
          <td>{contractOrProjectColumn}</td>
          <td className='invoice-table__fee-amount-column'>
            <b>{formatNumber(feeItemAmount)}</b> {currency}
          </td>
        </tr>
      )
    })
  }

  renderNoContent = () => {
    const noContentLabel = this.isProjectBased
      ? 'Invoice for this project was not created yet.'
      : 'Invoice for this contract was not created yet.'

    return (
      <NoContent label={noContentLabel}>
        <ACL roles={['Accounting', 'Admin']}>
          <Button primary onClick={this.handleDraftNewInvoice}>
            Draft new invoice
          </Button>
        </ACL>
      </NoContent>
    )
  }

  renderError = error => {
    const errorMessage = typeof error === 'string' ? error : error.message
    const defaultError = `Couldn't load the page, something went wrong`

    return (
      <h2 className='center-text m-t-xl m-b-xl'>
        {errorMessage || defaultError}
      </h2>
    )
  }

  render() {
    if (this.error) return this.renderError(this.error)

    const { invoices } = this.props.invoiceStore
    const { project } = this.props

    return (
      <DocumentTitle title={this.getTitle()}>
        <div className='invoice-wrapper animate-intro'>
          <div className='actions-wrapper clearfix'>
            <ul className='actions right-list'>
              <ACL roles={['Accounting', 'Admin']} action>
                <Action alignRight>
                  <li
                    onClick={this.handleDraftNewInvoice}
                    data-cy='draft-new-invoice-btn'
                  >
                    <AddIcon />
                    <span>Draft new invoice</span>
                  </li>
                </Action>
              </ACL>
            </ul>
          </div>

          {project && (!project.invoicingEntityId && project.projectSource === 'capi-mckinsey') &&
            <InfoBox theme="red">
              This project does not have an Invoicing Entity.
              No automated invoices will be created for this project.
          </InfoBox>
          }

          <h2>{this.invoiceTypeName} invoices and credit notes</h2>

          {invoices && invoices.length > 0 ? (
            <InvoicesTable invoices={invoices} />
          ) : (
              this.renderNoContent()
            )}

          <h2>Unallocated fees</h2>

          {this.renderUnallocatedFees()}
        </div>
      </DocumentTitle>
    )
  }
}

export default withRouter(
  inject(
    'modalStore',
    'invoiceStore',
    'staticData',
    'deliverableStore'
  )(observer(InvoiceList))
)
